import {
  ETIQUETA_TIPO_EXPORTACION,
  ETIQUETA_TIPO_EXPORTACION_EN,
  type ExportacionResumen,
  type OrdenSeguimiento,
} from '@cnc/contracts';
import {et, tr, txt} from '../i18n/idioma';
import {fecha, numero, textoOGuion} from '../utilidades/formato';
import {ayudaDeCampo} from './ayuda-seguimiento';
import type {ColumnaDeTabla, DefinicionDeColumnas} from './columnas';

export type ColumnaDeSeguimiento = ColumnaDeTabla<ExportacionResumen, OrdenSeguimiento>;

const G = {
  principal: txt('Principal', 'Main'),
  ruta: txt('Ruta y fechas', 'Route and dates'),
  cantidades: txt('Cantidades', 'Quantities'),
  comercial: txt('Comercial', 'Commercial'),
};
const GRUPOS = [G.principal, G.ruta, G.cantidades, G.comercial];

// Cada columna lleva su nota de ayuda (que es y como se llena), por su id.
const conAyuda = (columnas: ColumnaDeSeguimiento[]): ColumnaDeSeguimiento[] =>
  columnas.map(c => ({...c, ayuda: c.ayuda ?? ayudaDeCampo(c.id)}));

// Las columnas de la tabla de seguimiento de la app original, mas las que el
// modelo nuevo ya guarda (fechas reales, contenedores, comprador, incoterm).
export const COLUMNAS_DE_SEGUIMIENTO: ColumnaDeSeguimiento[] = conAyuda([
  {
    id: 'codigo',
    titulo: txt('Exportación', 'Export'),
    grupo: G.principal,
    orden: 'codigo',
    porDefecto: true,
    ancho: 140,
    valor: x => x.codigo,
  },
  {
    id: 'exportador',
    titulo: txt('Exportador', 'Exporter'),
    grupo: G.principal,
    orden: 'exportador',
    porDefecto: true,
    ancho: 130,
    valor: x => textoOGuion(x.exportador),
  },
  {
    id: 'tipo',
    titulo: txt('Tipo', 'Type'),
    grupo: G.principal,
    ancho: 100,
    valor: x => et(ETIQUETA_TIPO_EXPORTACION, ETIQUETA_TIPO_EXPORTACION_EN)[x.tipo],
  },
  {
    id: 'estado',
    titulo: txt('Estado', 'Status'),
    grupo: G.principal,
    orden: 'estado',
    porDefecto: true,
    ancho: 130,
    valor: x => x.estadoEtiqueta,
  },
  {
    id: 'agente',
    titulo: txt('Agente', 'Agent'),
    grupo: G.principal,
    orden: 'agente',
    porDefecto: true,
    ancho: 150,
    valor: x => textoOGuion(x.agente),
  },
  {
    id: 'naviera',
    titulo: txt('Naviera', 'Shipping line'),
    grupo: G.principal,
    orden: 'naviera',
    porDefecto: true,
    ancho: 150,
    valor: x => textoOGuion(x.naviera),
  },
  {
    id: 'booking',
    titulo: 'Booking',
    grupo: G.principal,
    orden: 'booking',
    porDefecto: true,
    ancho: 130,
    valor: x => textoOGuion(x.booking),
  },
  {
    id: 'buque',
    titulo: txt('Buque', 'Vessel'),
    grupo: G.principal,
    orden: 'buque',
    porDefecto: true,
    ancho: 150,
    valor: x => textoOGuion(x.buque),
  },
  {
    id: 'rastreo',
    titulo: txt('Rastreo', 'Tracking link'),
    grupo: G.principal,
    porDefecto: true,
    ancho: 90,
    valor: x => (x.enlaceNaviera ? tr('Naviera', 'Shipping line') : '—'),
  },

  {
    id: 'puertoCarga',
    titulo: txt('Puerto de carga', 'Port of loading'),
    grupo: G.ruta,
    orden: 'puertoCarga',
    porDefecto: true,
    ancho: 130,
    valor: x => textoOGuion(x.puertoCarga),
  },
  {
    id: 'puertoDestino',
    titulo: txt('Destino', 'Destination'),
    grupo: G.ruta,
    orden: 'puertoDestino',
    porDefecto: true,
    ancho: 120,
    valor: x => textoOGuion(x.puertoDestino),
  },
  {
    id: 'etd',
    titulo: 'ETD',
    grupo: G.ruta,
    orden: 'etd',
    porDefecto: true,
    ancho: 105,
    valor: x => fecha(x.etd),
  },
  {
    id: 'etaMundra',
    titulo: 'ETA Mundra',
    grupo: G.ruta,
    orden: 'etaMundra',
    porDefecto: true,
    ancho: 110,
    valor: x => fecha(x.etaMundra),
  },
  {
    id: 'etaSachana',
    titulo: 'ETA Sachana',
    grupo: G.ruta,
    orden: 'etaSachana',
    porDefecto: true,
    ancho: 110,
    valor: x => fecha(x.etaSachana),
  },
  {
    id: 'etaHindustan',
    titulo: 'ETA Hindustan',
    grupo: G.ruta,
    orden: 'etaHindustan',
    porDefecto: true,
    ancho: 120,
    valor: x => fecha(x.etaHindustan),
  },
  {
    id: 'eta',
    titulo: txt('ETA destino', 'ETA destination'),
    grupo: G.ruta,
    orden: 'eta',
    ancho: 110,
    valor: x => fecha(x.eta),
  },
  {
    id: 'etaInicial',
    titulo: txt('ETA inicial', 'Initial ETA'),
    grupo: G.ruta,
    ancho: 110,
    valor: x => fecha(x.etaInicial),
  },
  {
    id: 'diasParaLlegada',
    titulo: txt('Días para llegar', 'Days to arrival'),
    grupo: G.ruta,
    numerica: true,
    ancho: 110,
    valor: x => numero(x.diasParaLlegada),
  },
  {
    id: 'fechaZarpe',
    titulo: txt('Zarpe real', 'Actual departure'),
    grupo: G.ruta,
    ancho: 105,
    valor: x => fecha(x.fechaZarpe),
  },
  {
    id: 'fechaLlegada',
    titulo: txt('Llegada real', 'Actual arrival'),
    grupo: G.ruta,
    orden: 'fechaLlegada',
    ancho: 110,
    valor: x => fecha(x.fechaLlegada),
  },
  {
    id: 'fechaTren',
    titulo: txt('Fecha en tren', 'Rail date'),
    grupo: G.ruta,
    ancho: 110,
    valor: x => fecha(x.fechaTren),
  },
  {
    id: 'puertoActual',
    titulo: txt('Puerto actual', 'Current port'),
    grupo: G.ruta,
    ancho: 150,
    valor: x => x.puertoActual ?? '—',
  },
  {
    id: 'etdPuertoActual',
    titulo: txt('ETD puerto actual', 'Current port ETD'),
    grupo: G.ruta,
    ancho: 120,
    valor: x => fecha(x.etdPuertoActual),
  },
  {
    id: 'fechaLlegadaHindustan',
    titulo: txt('Llegada Hindustan', 'Arrival Hindustan'),
    grupo: G.ruta,
    ancho: 130,
    valor: x => fecha(x.fechaLlegadaHindustan),
  },
  {
    id: 'fechaBl',
    titulo: txt('Fecha BL', 'BL date'),
    grupo: G.ruta,
    orden: 'fechaBl',
    ancho: 105,
    valor: x => fecha(x.fechaBl),
  },
  {
    id: 'fechaLiquidacion',
    titulo: txt('Liquidación', 'Settlement'),
    grupo: G.ruta,
    ancho: 110,
    valor: x => fecha(x.fechaLiquidacion),
  },
  {
    id: 'diasLibres',
    titulo: txt('Días libres', 'Free days'),
    grupo: G.ruta,
    orden: 'diasLibres',
    porDefecto: true,
    numerica: true,
    ancho: 95,
    valor: x => numero(x.diasLibres),
  },
  {
    id: 'diasEnTransito',
    titulo: txt('Días en tránsito', 'Days in transit'),
    grupo: G.ruta,
    orden: 'diasEnTransito',
    porDefecto: true,
    numerica: true,
    ancho: 115,
    valor: x => numero(x.diasEnTransito),
  },
  {
    id: 'diasEnTransitoInicial',
    titulo: txt('Días en tránsito planeados', 'Planned days in transit'),
    grupo: G.ruta,
    porDefecto: false,
    numerica: true,
    ancho: 130,
    valor: x => numero(x.diasEnTransitoInicial),
  },

  {
    id: 'bigBags',
    titulo: 'BB',
    grupo: G.cantidades,
    orden: 'bigBags',
    porDefecto: true,
    numerica: true,
    ancho: 80,
    valor: x => numero(x.bigBags),
  },
  // El numero que trae el Status (Tracking Status, BB QTY). "BB" muestra las
  // bolsas asignadas cuando las hay, asi que este se ve aparte (7/10/2026).
  {
    id: 'bigBagsReportados',
    titulo: txt('BB reportados', 'Reported BB'),
    grupo: G.cantidades,
    porDefecto: true,
    numerica: true,
    ancho: 100,
    valor: x => (x.bigBagsReportados === null ? '—' : numero(x.bigBagsReportados)),
  },
  {
    id: 'contenedores',
    titulo: txt('Contenedores', 'Containers'),
    grupo: G.cantidades,
    numerica: true,
    ancho: 110,
    valor: x => numero(x.contenedores),
  },
  {
    id: 'tonHumedas',
    titulo: 'WMT',
    grupo: G.cantidades,
    orden: 'tonHumedas',
    numerica: true,
    ancho: 100,
    valor: x => numero(x.tonHumedas, 3),
  },
  {
    id: 'tonSecas',
    titulo: 'DMT',
    grupo: G.cantidades,
    orden: 'tonSecas',
    porDefecto: true,
    numerica: true,
    ancho: 100,
    valor: x => numero(x.tonSecas, 3),
  },
  {
    id: 'auG',
    titulo: 'Au (g)',
    grupo: G.cantidades,
    orden: 'auG',
    porDefecto: true,
    numerica: true,
    ancho: 110,
    valor: x => numero(x.auG, 2),
  },
  {
    id: 'auOz',
    titulo: 'Au (oz)',
    grupo: G.cantidades,
    orden: 'auOz',
    porDefecto: true,
    numerica: true,
    ancho: 100,
    valor: x => numero(x.auOz, 2),
  },
  {
    id: 'agG',
    titulo: 'Ag (g)',
    grupo: G.cantidades,
    orden: 'agG',
    porDefecto: true,
    numerica: true,
    ancho: 110,
    valor: x => numero(x.agG, 2),
  },
  {
    id: 'agOz',
    titulo: 'Ag (oz)',
    grupo: G.cantidades,
    orden: 'agOz',
    porDefecto: true,
    numerica: true,
    ancho: 100,
    valor: x => numero(x.agOz, 2),
  },

  {
    id: 'comprador',
    titulo: txt('Comprador', 'Buyer'),
    grupo: G.comercial,
    ancho: 150,
    valor: x => textoOGuion(x.comprador),
  },
  {id: 'incoterm', titulo: 'Incoterm', grupo: G.comercial, ancho: 90, valor: x => textoOGuion(x.incoterm)},
  {
    id: 'valorAproximadoUsd',
    titulo: txt('Valor aprox. (USD)', 'Approx. value (USD)'),
    grupo: G.comercial,
    orden: 'valorAproximadoUsd',
    porDefecto: true,
    numerica: true,
    ancho: 130,
    valor: x => numero(x.valorAproximadoUsd, 2),
  },
  {
    id: 'facturadora',
    titulo: txt('Empresa que factura', 'Invoicing company'),
    grupo: G.comercial,
    porDefecto: true,
    ancho: 150,
    valor: x => textoOGuion(x.facturadora),
  },
  {
    id: 'factura',
    titulo: txt('Factura', 'Invoice'),
    grupo: G.comercial,
    porDefecto: true,
    ancho: 130,
    valor: x => textoOGuion(x.factura),
  },
  {
    id: 'xau',
    titulo: 'XAU',
    grupo: G.comercial,
    orden: 'xau',
    porDefecto: true,
    numerica: true,
    ancho: 100,
    valor: x => numero(x.xau, 2),
  },
  {
    id: 'xag',
    titulo: 'XAG',
    grupo: G.comercial,
    orden: 'xag',
    porDefecto: true,
    numerica: true,
    ancho: 100,
    valor: x => numero(x.xag, 2),
  },
  {
    id: 'fechaLbma',
    titulo: 'LBMA',
    grupo: G.comercial,
    orden: 'fechaLbma',
    porDefecto: true,
    ancho: 105,
    valor: x => fecha(x.fechaLbma),
  },
  {
    id: 'financiadaPorBlq',
    titulo: txt('Financiada por BLQ', 'Financed by BLQ'),
    grupo: G.comercial,
    ancho: 130,
    valor: x => textoOGuion(x.financiadaPorBlq),
  },
]);

export const DEFINICION_SEGUIMIENTO: DefinicionDeColumnas<ExportacionResumen, OrdenSeguimiento> = {
  clave: 'cnc.seguimiento.columnas',
  columnas: COLUMNAS_DE_SEGUIMIENTO,
  grupos: GRUPOS,
  fija: 'codigo',
};
